import React, { ChangeEvent, useCallback, useEffect, useMemo, useState } from "react";
import ExcelJS from "exceljs/dist/exceljs.min.js";
import * as XLSX from "xlsx";
import { AlertCircle, DollarSign, Download, Edit3, Plus, RefreshCw, Search, Trash2, Upload, X } from "lucide-react";
import ToasterWithMessage from "../../../components/shared-components/toaster";
import {
  BuyAdminApiError,
  BuyAdminTicketSavingsConflict,
  BuyAdminTicketSavingsConflictDetails,
  BuyAdminTicketSavingsImportError,
  BuyAdminTicketSavingsInput,
  BuyAdminTicketSavingsRecord,
  BuyAdminPerformerTeamCatalogEntry,
  buyAdminApi,
} from "../../../services/buyAdminApi";
import BuyAdminThemeToggle from "../shared/BuyAdminThemeToggle";
import "../shared/buyAdminThemeGlobals.css";
import styles from "./TicketSavings.module.css";

type FormState = {
  league: string;
  team: string;
  low: string;
  high: string;
  median: string;
  recommended: string;
  showToUserMin: string;
  showToUserMax: string;
  priceRangeMin: string;
  priceRangeMax: string;
};

type ImportSummary = {
  insertedCount: number;
  failedCount: number;
  errors: BuyAdminTicketSavingsImportError[];
} | null;

const ROWS_PER_PAGE = 6;

const emptyForm: FormState = {
  league: "",
  team: "",
  low: "",
  high: "",
  median: "",
  recommended: "",
  showToUserMin: "",
  showToUserMax: "",
  priceRangeMin: "",
  priceRangeMax: "",
};

const requiredHeaders = [
  "League",
  "Team",
  "Low",
  "High",
  "Median",
  "Recommended",
  "Show To User Min",
  "Show To User Max",
  "Price Range Min",
  "Price Range Max",
];

const normalizeHeader = (value: unknown) => String(value ?? "").trim().toLowerCase().replace(/\s+/g, " ");
const normalizeInfinity = (value: string) => value.trim().toLowerCase();
const isInfinityValue = (value: string) => ["infinity", "infinite", "inf"].includes(normalizeInfinity(value));

const parseDecimal = (value: string): number | null => {
  if (value.trim() === "") return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? Number(parsed.toFixed(2)) : null;
};

const isNullLikePercentage = (value: string) => {
  const normalized = value.trim().toLowerCase();
  return normalized === "" || normalized === "null" || normalized === "no data";
};

const parseNullablePercentage = (value: string): number | null => {
  if (isNullLikePercentage(value)) return null;
  const normalized = value.trim().replace(/%\s*$/, "").trim();
  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? Number(parsed.toFixed(2)) : null;
};

const parsePercentage = (value: string): number | null => {
  const normalized = value.trim().replace(/%\s*$/, "").trim();
  if (!normalized) return null;
  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? Number(parsed.toFixed(2)) : null;
};

const formatNumber = (value: number | null, isInfinite = false) => {
  if (isInfinite) return "Infinity";
  if (value === null) return "-";
  return Number.isInteger(value) ? String(value) : value.toFixed(2).replace(/\.?0+$/, "");
};

const formatNullableFormValue = (value: number | null) => value === null ? "" : formatNumber(value);

const formatPercent = (value: number) => `${formatNumber(value)}%`;

const formatDate = (value: string | null) => {
  if (!value) return "-";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString();
};

const buildPayloadFromForm = (form: FormState): BuyAdminTicketSavingsInput | null => {
  const low = parseNullablePercentage(form.low);
  const high = parseNullablePercentage(form.high);
  const median = parseNullablePercentage(form.median);
  const showToUserMin = parsePercentage(form.showToUserMin);
  const showToUserMax = parsePercentage(form.showToUserMax);
  const priceRangeMin = parseDecimal(form.priceRangeMin);
  const priceRangeMaxIsInfinite = isInfinityValue(form.priceRangeMax);
  const priceRangeMax = priceRangeMaxIsInfinite ? "Infinity" : parseDecimal(form.priceRangeMax);

  if (
    showToUserMin == null ||
    showToUserMax == null ||
    priceRangeMin == null ||
    (!priceRangeMaxIsInfinite && priceRangeMax == null)
  ) {
    return null;
  }

  return {
    league: form.league.trim(),
    team: form.team.trim(),
    low,
    high,
    median,
    recommended: form.recommended.trim(),
    showToUserMin,
    showToUserMax,
    priceRangeMin,
    priceRangeMax,
    priceRangeMaxIsInfinite,
  };
};

const validateForm = (form: FormState): string[] => {
  const errors: string[] = [];
  const payload = buildPayloadFromForm(form);

  if (!form.league.trim()) errors.push("League is required.");
  if (!form.recommended.trim()) errors.push("Recommended is required.");

  const low = parseNullablePercentage(form.low);
  const high = parseNullablePercentage(form.high);
  const median = parseNullablePercentage(form.median);
  const showToUserMin = parsePercentage(form.showToUserMin);
  const showToUserMax = parsePercentage(form.showToUserMax);
  const priceRangeMin = parseDecimal(form.priceRangeMin);
  const priceRangeMaxIsInfinite = isInfinityValue(form.priceRangeMax);
  const priceRangeMax = priceRangeMaxIsInfinite ? null : parseDecimal(form.priceRangeMax);

  if (!form.team.trim() && !priceRangeMaxIsInfinite) {
    errors.push("League defaults (empty Team) must use Price Range Max of Infinity.");
  }

  if (!isNullLikePercentage(form.low) && low == null) errors.push("Low must be numeric, a percentage, null, or No data.");
  if (!isNullLikePercentage(form.high) && high == null) errors.push("High must be numeric, a percentage, null, or No data.");
  if (!isNullLikePercentage(form.median) && median == null) errors.push("Median must be numeric, a percentage, null, or No data.");
  if (showToUserMin == null) errors.push("Show To User Min must be numeric or a percentage.");
  if (showToUserMax == null) errors.push("Show To User Max must be numeric or a percentage.");
  if (priceRangeMin == null) errors.push("Price Range Min must be numeric.");
  if (!priceRangeMaxIsInfinite && priceRangeMax == null) errors.push("Price Range Max must be numeric or Infinity.");

  if (low != null && low < 0) errors.push("Low must be greater than or equal to 0.");
  if (high != null && high < 0) errors.push("High must be greater than or equal to 0.");
  if (median != null && median < 0) errors.push("Median must be greater than or equal to 0.");
  if (showToUserMin != null && (showToUserMin < 0 || showToUserMin > 100)) {
    errors.push("Show To User Min must be between 0 and 100.");
  }
  if (showToUserMax != null && (showToUserMax < 0 || showToUserMax > 100)) {
    errors.push("Show To User Max must be between 0 and 100.");
  }
  if (showToUserMin != null && showToUserMax != null && showToUserMin > showToUserMax) {
    errors.push("Show To User Min must be less than or equal to Show To User Max.");
  }
  if (priceRangeMin != null && priceRangeMin < 0) {
    errors.push("Price Range Min must be greater than or equal to 0.");
  }
  if (!priceRangeMaxIsInfinite && priceRangeMin != null && priceRangeMax != null && priceRangeMax < priceRangeMin) {
    errors.push("Price Range Max must be greater than or equal to Price Range Min.");
  }

  if (!payload && !errors.length) {
    errors.push("Please complete all required numeric fields.");
  }

  return errors;
};

const toFormState = (row?: BuyAdminTicketSavingsRecord | null): FormState =>
  row
    ? {
        showToUserMin: formatNumber(row.show_to_user_min),
        showToUserMax: formatNumber(row.show_to_user_max),
        priceRangeMin: formatNumber(row.price_range_min),
        priceRangeMax: formatNumber(row.price_range_max, row.price_range_max_is_infinite),
        recommended: row.recommended,
        low: formatNullableFormValue(row.low),
        high: formatNullableFormValue(row.high),
        median: formatNullableFormValue(row.median),
        league: row.league,
        team: row.team || "",
      }
    : { ...emptyForm };

const renderConflictSummary = (conflict: BuyAdminTicketSavingsConflict) => (
  <>
    <p><strong>League:</strong> {conflict.league}</p>
    <p><strong>Team:</strong> {conflict.team || "(League default)"}</p>
    <p>
      <strong>Price Range:</strong> {formatNumber(conflict.priceRangeMin)} -{" "}
      {formatNumber(conflict.priceRangeMax, conflict.priceRangeMaxIsInfinite)}
    </p>
    <p><strong>Show To User:</strong> {formatPercent(conflict.showToUserMin)} - {formatPercent(conflict.showToUserMax)}</p>
    <p><strong>Low / High / Median:</strong> {formatNumber(conflict.low)} / {formatNumber(conflict.high)} / {formatNumber(conflict.median)}</p>
    <p><strong>Recommended:</strong> {conflict.recommended}</p>
  </>
);

const BuyAdminTicketSavings = () => {
  const { success, error, info } = ToasterWithMessage();
  const [rows, setRows] = useState<BuyAdminTicketSavingsRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [importing, setImporting] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingRow, setEditingRow] = useState<BuyAdminTicketSavingsRecord | null>(null);
  const [form, setForm] = useState<FormState>({ ...emptyForm });
  const [formErrors, setFormErrors] = useState<string[]>([]);
  const [conflictDetails, setConflictDetails] = useState<BuyAdminTicketSavingsConflictDetails | null>(null);
  const [importSummary, setImportSummary] = useState<ImportSummary>(null);
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const [selectedImportFile, setSelectedImportFile] = useState<File | null>(null);
  const [importModalError, setImportModalError] = useState("");
  const [refreshing, setRefreshing] = useState(false);
  const [teamCatalog, setTeamCatalog] = useState<BuyAdminPerformerTeamCatalogEntry[]>([]);
  const [catalogLoading, setCatalogLoading] = useState(false);
  const [catalogError, setCatalogError] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");

  const loadRows = useCallback(async () => {
    setLoading(true);
    setLoadError("");
    try {
      const response = await buyAdminApi.getTicketSavings();
      setRows(response.data || []);
    } catch (err: any) {
      setLoadError(err?.message || "Failed to load Ticket Savings records.");
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadTeamCatalog = useCallback(async () => {
    setCatalogLoading(true);
    setCatalogError("");
    try {
      const response = await buyAdminApi.getPerformerTeamCatalog();
      setTeamCatalog(response.data || []);
    } catch (err: any) {
      setTeamCatalog([]);
      setCatalogError(err?.message || "Failed to load the league and team catalog.");
    } finally {
      setCatalogLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadRows();
    void loadTeamCatalog();
  }, [loadRows, loadTeamCatalog]);

  const hasImportErrors = Boolean(importSummary?.failedCount);

  const filteredRows = useMemo(() => {
    const query = searchTerm.trim().toLocaleLowerCase();
    if (!query) return rows;

    return rows.filter((row) =>
      row.league.toLocaleLowerCase().includes(query) ||
      (row.team || "").toLocaleLowerCase().includes(query)
    );
  }, [rows, searchTerm]);

  const pageCount = Math.max(1, Math.ceil(filteredRows.length / ROWS_PER_PAGE));

  useEffect(() => {
    setCurrentPage((page) => Math.min(page, pageCount));
  }, [pageCount]);

  const paginatedRows = useMemo(() => {
    const startIndex = (currentPage - 1) * ROWS_PER_PAGE;
    return filteredRows.slice(startIndex, startIndex + ROWS_PER_PAGE);
  }, [currentPage, filteredRows]);

  const allSelected = filteredRows.length > 0 &&
    filteredRows.every((row) => selectedIds.includes(row.id));

  const selectedLabel = useMemo(() => {
    if (!selectedIds.length) return "";
    return `${selectedIds.length} selected`;
  }, [selectedIds.length]);

  const leagueOptions = useMemo(() => {
    const leagues = new Set(teamCatalog.map((entry) => entry.category_name));
    if (form.league) leagues.add(form.league);
    return Array.from(leagues).sort((left, right) => left.localeCompare(right));
  }, [form.league, teamCatalog]);

  const teamOptions = useMemo(() => {
    const teams = teamCatalog
      .filter((entry) => entry.category_name === form.league)
      .map((entry) => entry.name);
    if (form.team && !teams.includes(form.team)) teams.push(form.team);
    return Array.from(new Set(teams)).sort((left, right) => left.localeCompare(right));
  }, [form.league, form.team, teamCatalog]);

  const resetModalState = () => {
    setIsModalOpen(false);
    setEditingRow(null);
    setForm({ ...emptyForm });
    setFormErrors([]);
    setConflictDetails(null);
  };

  const openAddModal = () => {
    setEditingRow(null);
    setForm({ ...emptyForm });
    setFormErrors([]);
    setConflictDetails(null);
    setIsModalOpen(true);
  };

  const openEditModal = (row: BuyAdminTicketSavingsRecord) => {
    setEditingRow(row);
    setForm(toFormState(row));
    setFormErrors([]);
    setConflictDetails(null);
    setIsModalOpen(true);
  };

  const handleDelete = async (row: BuyAdminTicketSavingsRecord) => {
    if (deletingId !== null) return;
    setDeletingId(row.id);
    try {
      await buyAdminApi.deleteTicketSavings(row.id);
      setRows((current) => current.filter((record) => record.id !== row.id));
      setSelectedIds((current) => current.filter((id) => id !== row.id));
      success("Ticket Savings record deleted successfully.");
    } catch (err: any) {
      error(err?.message || "Failed to delete Ticket Savings record.");
    } finally {
      setDeletingId(null);
    }
  };

  const toggleRow = (id: number) => {
    setSelectedIds((current) => (current.includes(id) ? current.filter((value) => value !== id) : [...current, id]));
  };

  const toggleAll = () => {
    const filteredIds = new Set(filteredRows.map((row) => row.id));
    setSelectedIds((current) => allSelected
      ? current.filter((id) => !filteredIds.has(id))
      : Array.from(new Set([...current, ...filteredIds])));
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    setCurrentPage(1);
    await loadRows();
    window.setTimeout(() => setRefreshing(false), 500);
  };

  const handleFormChange = (field: keyof FormState) => (event: ChangeEvent<HTMLInputElement>) => {
    setForm((current) => ({ ...current, [field]: event.target.value }));
  };

  const handleLeagueChange = (event: ChangeEvent<HTMLSelectElement>) => {
    const league = event.target.value;
    setForm((current) => ({ ...current, league, team: "" }));
  };

  const handleTeamChange = (event: ChangeEvent<HTMLSelectElement>) => {
    setForm((current) => ({ ...current, team: event.target.value }));
  };

  const handleSave = async () => {
    const validationErrors = validateForm(form);
    const payload = buildPayloadFromForm(form);
    if (validationErrors.length || !payload) {
      setFormErrors(validationErrors.length ? validationErrors : ["Please fill in all required fields."]);
      setConflictDetails(null);
      return;
    }

    setSaving(true);
    setFormErrors([]);
    setConflictDetails(null);

    try {
      if (editingRow) {
        await buyAdminApi.updateTicketSavings(editingRow.id, payload);
        success("Ticket Savings record updated successfully.");
      } else {
        await buyAdminApi.createTicketSavings(payload);
        success("Ticket Savings record created successfully.");
      }
      setCurrentPage(1);
      resetModalState();
      await loadRows();
    } catch (err: any) {
      if (err instanceof BuyAdminApiError) {
        if (Array.isArray(err.details)) {
          setFormErrors(err.details as string[]);
        } else if (err.code === "PRICE_RANGE_CONFLICT" && err.details) {
          setFormErrors([err.message]);
          setConflictDetails(err.details as BuyAdminTicketSavingsConflictDetails);
        } else {
          setFormErrors([err.message]);
        }
      } else {
        setFormErrors([err?.message || "Failed to save Ticket Savings record."]);
      }
    } finally {
      setSaving(false);
    }
  };

  const openImportModal = () => {
    setSelectedImportFile(null);
    setImportModalError("");
    setImportSummary(null);
    setIsImportModalOpen(true);
  };

  const closeImportModal = () => {
    if (importing) return;
    setIsImportModalOpen(false);
    setSelectedImportFile(null);
    setImportModalError("");
  };

  const handleExport = async () => {
    if (!rows.length) {
      info("There are no Ticket Savings records to export.");
      return;
    }

    try {
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet("Ticket Savings");
      worksheet.addRow(requiredHeaders);
      worksheet.getRow(1).font = { bold: true };
      worksheet.views = [{ state: "frozen", ySplit: 1 }];
      worksheet.autoFilter = { from: "A1", to: `J${rows.length + 1}` };
      worksheet.columns = requiredHeaders.map((header) => ({
        header,
        key: header,
        width: Math.max(header.length + 3, 16),
      }));

      rows.forEach((row) => {
        worksheet.addRow({
          League: row.league,
          Team: row.team || "",
          Low: row.low ?? "No data",
          High: row.high ?? "No data",
          Median: row.median ?? "No data",
          Recommended: row.recommended,
          "Show To User Min": row.show_to_user_min,
          "Show To User Max": row.show_to_user_max,
          "Price Range Min": row.price_range_min,
          "Price Range Max": row.price_range_max_is_infinite ? "Infinity" : row.price_range_max,
        });
      });

      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
      const downloadUrl = URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = downloadUrl;
      anchor.download = "ticket-savings-export.xlsx";
      anchor.click();
      URL.revokeObjectURL(downloadUrl);
      success(`Exported ${rows.length} Ticket Savings records.`);
    } catch (err: any) {
      error(err?.message || "Failed to export Ticket Savings records.");
    }
  };
  const handleImportFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    setSelectedImportFile(event.target.files?.[0] || null);
    setImportModalError("");
  };

  const handleDownloadSampleSheet = async () => {
    try {
      const catalogResponse = teamCatalog.length
        ? { data: teamCatalog }
        : await buyAdminApi.getPerformerTeamCatalog();
      const catalog = catalogResponse.data || [];
      if (!catalog.length) {
        error("No team catalog entries are available for the sample sheet.");
        return;
      }

      const teamsByLeague = catalog.reduce<Map<string, string[]>>((result, entry) => {
        const teams = result.get(entry.category_name) || [];
        if (!teams.includes(entry.name)) teams.push(entry.name);
        result.set(entry.category_name, teams);
        return result;
      }, new Map());
      const leagues = Array.from(teamsByLeague.keys()).sort((left, right) => left.localeCompare(right));
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet("Ticket Savings");
      const listSheet = workbook.addWorksheet("Template Lists");
      listSheet.state = "veryHidden";

      worksheet.addRow(requiredHeaders);
      worksheet.getRow(1).font = { bold: true };
      worksheet.views = [{ state: "frozen", ySplit: 1 }];
      worksheet.columns = requiredHeaders.map((header) => ({ header, key: header, width: Math.max(header.length + 3, 16) }));

      listSheet.getCell("A1").value = "League";
      listSheet.getCell("B1").value = "Team Range";
      listSheet.getCell("C1").value = "League List";
      leagues.forEach((league, index) => {
        const row = index + 2;
        const column = index + 5;
        const columnLetter = listSheet.getColumn(column).letter;
        const rangeName = `TicketSavingsTeams${index + 1}`;
        const teams = (teamsByLeague.get(league) || []).sort((left, right) => left.localeCompare(right));
        listSheet.getCell(`A${row}`).value = league;
        listSheet.getCell(`B${row}`).value = rangeName;
        listSheet.getCell(`C${row}`).value = league;
        teams.forEach((team, teamIndex) => {
          listSheet.getCell(`${columnLetter}${teamIndex + 2}`).value = team;
        });
        workbook.definedNames.add(`'Template Lists'!$${columnLetter}$2:$${columnLetter}$${teams.length + 1}`, rangeName);
      });

      const leagueValidationRange = `'Template Lists'!$C$2:$C$${leagues.length + 1}`;
      for (let row = 2; row <= 1001; row += 1) {
        worksheet.getCell(`A${row}`).dataValidation = {
          type: "list",
          allowBlank: true,
          formulae: [leagueValidationRange],
        };
        worksheet.getCell(`B${row}`).dataValidation = {
          type: "list",
          allowBlank: true,
          formulae: [`INDIRECT(IFERROR(VLOOKUP($A${row},'Template Lists'!$A$2:$B$${leagues.length + 1},2,FALSE),""))`],
        };
      }

      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
      const downloadUrl = URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = downloadUrl;
      anchor.download = "ticket-savings-import-template.xlsx";
      anchor.click();
      URL.revokeObjectURL(downloadUrl);
    } catch (err: any) {
      error(err?.message || "Failed to create the Ticket Savings sample sheet.");
    }
  };

  const handleImportSubmit = async () => {
    const file = selectedImportFile;
    if (!file) {
      setImportModalError("Select an Excel file before submitting.");
      return;
    }

    setImportSummary(null);
    setImportModalError("");
    setImporting(true);

    try {
      const workbook = XLSX.read(await file.arrayBuffer(), { type: "array" });
      const sheet = workbook.Sheets[workbook.SheetNames[0]];
      const rowsMatrix = XLSX.utils.sheet_to_json<(string | number | null)[]>(sheet, {
        header: 1,
        defval: "",
        raw: false,
      });

      const headerRow = rowsMatrix[0] || [];
      const headerIndex = new Map<string, number>();
      headerRow.forEach((cell, index) => {
        const normalized = normalizeHeader(cell);
        if (normalized) {
          headerIndex.set(normalized, index);
        }
      });

      const missingHeaders = requiredHeaders.filter((header) => !headerIndex.has(normalizeHeader(header)));
      if (missingHeaders.length) {
        setImportModalError(`Missing Excel columns: ${missingHeaders.join(", ")}`);
        return;
      }

      const payloadRows = rowsMatrix
        .slice(1)
        .map((row, rowIndex) => {
          const hasValues = row.some((cell) => String(cell ?? "").trim() !== "");
          if (!hasValues) return null;

          const nextRow: Record<string, unknown> = { __rowNumber: rowIndex + 2 };
          requiredHeaders.forEach((header) => {
            nextRow[header] = row[headerIndex.get(normalizeHeader(header)) || 0];
          });
          return nextRow;
        })
        .filter((row): row is Record<string, unknown> => row !== null);

      if (!payloadRows.length) {
        setImportModalError("The selected Excel file does not contain any data rows.");
        return;
      }

      const response = await buyAdminApi.importTicketSavings({ rows: payloadRows });
      const summary = response.data;
      setCurrentPage(1);
      setImportSummary(summary);
      await loadRows();
      setIsImportModalOpen(false);
      setSelectedImportFile(null);

      if (summary.failedCount > 0) {
        info(`Import completed. Inserted ${summary.insertedCount}, failed ${summary.failedCount}.`);
      } else {
        success(`Import completed. Inserted ${summary.insertedCount} Ticket Savings records.`);
      }
    } catch (err: any) {
      setImportModalError(err?.message || "Failed to import Ticket Savings Excel.");
    } finally {
      setImporting(false);
    }
  };

  return (
    <div className={`${styles.page} buy-admin-theme-scope`}>
      <div className={styles.header}>
        <div>
          <div className={styles.titleRow}>
            <DollarSign size={22} />
            <h1>Ticket Savings</h1>
          </div>
          <p className={styles.subtitle}>
            Manage buy-side savings by league/team price ranges. Team default = Team + 0–Infinity.
            League default = empty Team + 0–Infinity. Defaults use Show To User Min/Max (e.g. 3 and 3 for 3%).
          </p>
        </div>

        <div className={styles.headerActions}>
          <button type="button" className={styles.button} onClick={openImportModal} disabled={importing}>
            <Upload size={16} />
            Import Excel
          </button>
          <button type="button" className={styles.button} onClick={handleDownloadSampleSheet}>
            <Download size={16} />
            Download Sample Sheet
          </button>
          <button type="button" className={styles.button} onClick={() => void handleExport()} disabled={loading || !rows.length}>
            <Download size={16} />
            Export All Data
          </button>
          <button type="button" className={styles.primaryButton} onClick={openAddModal}>
            <Plus size={16} />
            Add Row
          </button>
          <button type="button" className={styles.refreshButton} onClick={() => void handleRefresh()} aria-label="Refresh ticket savings" title="Refresh">
            <RefreshCw size={16} className={refreshing ? styles.spin : ""} />
          </button>
          <BuyAdminThemeToggle iconOnly />
        </div>
      </div>

      {selectedLabel ? <div className={styles.summaryBar}>{selectedLabel}</div> : null}

      {loadError ? (
        <div className={styles.errorBanner}>
          <AlertCircle size={16} />
          <span>{loadError}</span>
        </div>
      ) : null}

      {importSummary ? (
        <div className={importSummary.failedCount > 0 ? styles.infoBanner : styles.successBanner}>
          <AlertCircle size={16} />
          <div>
            <div>Import completed.</div>
            <div className={styles.importSummaryMeta}>
              <span>Successfully inserted: {importSummary.insertedCount}</span>
              <span>Failed validation: {importSummary.failedCount}</span>
            </div>
          </div>
        </div>
      ) : null}

      {!hasImportErrors ? (
        <>
          <div className={styles.searchToolbar}>
            <label className={styles.searchField}>
              <Search size={17} aria-hidden="true" />
              <span className={styles.visuallyHidden}>Search by team or league</span>
              <input
                type="search"
                value={searchTerm}
                onChange={(event) => {
                  setSearchTerm(event.target.value);
                  setCurrentPage(1);
                }}
                placeholder="Search by team or league"
              />
              {searchTerm ? (
                <button
                  type="button"
                  className={styles.clearSearchButton}
                  onClick={() => {
                    setSearchTerm("");
                    setCurrentPage(1);
                  }}
                  aria-label="Clear search"
                  title="Clear search"
                >
                  <X size={15} />
                </button>
              ) : null}
            </label>
            <span className={styles.searchResultCount}>
              {searchTerm.trim()
                ? `${filteredRows.length} matching record${filteredRows.length === 1 ? "" : "s"}`
                : `${rows.length} total record${rows.length === 1 ? "" : "s"}`}
            </span>
          </div>

          <div className={styles.tableWrap}>
            <table className={styles.table}>
          <thead>
            <tr>
              <th className={styles.checkboxCell}>
                <input type="checkbox" checked={allSelected} onChange={toggleAll} disabled={!filteredRows.length || loading} aria-label="Select all matching ticket savings rows" />
              </th>
              <th>League</th>
              <th>Team</th>
              <th>Low</th>
              <th>High</th>
              <th>Median</th>
              <th>Recommended</th>
              <th>Show To User Min</th>
              <th>Show To User Max</th>
              <th>Price Range Min</th>
              <th>Price Range Max</th>
              <th>Created By</th>
              <th>Created At</th>
              <th>Updated By</th>
              <th>Updated At</th>
              <th className={styles.actionsCell}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={16} className={styles.loadingState}>Loading Ticket Savings...</td>
              </tr>
            ) : null}
            {!loading &&
              paginatedRows.map((row) => (
                <tr key={row.id}>
                  <td className={styles.checkboxCell}>
                    <input
                      type="checkbox"
                      checked={selectedIds.includes(row.id)}
                      onChange={() => toggleRow(row.id)}
                      aria-label={`Select ${row.league} ${row.team || "league-default"} ${row.id}`}
                    />
                  </td>
                  <td>{row.league}</td>
                  <td>{row.team || "(League default)"}</td>
                  <td>{formatNumber(row.low)}</td>
                  <td>{formatNumber(row.high)}</td>
                  <td>{formatNumber(row.median)}</td>
                  <td>{row.recommended}</td>
                  <td>{formatPercent(row.show_to_user_min)}</td>
                  <td>{formatPercent(row.show_to_user_max)}</td>
                  <td>{formatNumber(row.price_range_min)}</td>
                  <td>{formatNumber(row.price_range_max, row.price_range_max_is_infinite)}</td>
                  <td>{row.created_by || "-"}</td>
                  <td>{formatDate(row.created_at)}</td>
                  <td>{row.updated_by || "-"}</td>
                  <td>{formatDate(row.updated_at)}</td>
                  <td className={styles.actionsCell}>
                    <div className={styles.rowActions}>
                      <button type="button" className={styles.editButton} onClick={() => openEditModal(row)} disabled={deletingId === row.id}>
                        <Edit3 size={15} />
                        Edit
                      </button>
                      <button
                        type="button"
                        className={`${styles.editButton} ${styles.deleteButton}`}
                        onClick={() => void handleDelete(row)}
                        disabled={deletingId !== null}
                        aria-label={`Delete Ticket Savings for ${row.league}${row.team ? ` - ${row.team}` : ""}, row ${row.id}`}
                      >
                        <Trash2 size={15} />
                        {deletingId === row.id ? "Deleting..." : "Delete"}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            {!loading && !filteredRows.length ? (
              <tr>
                <td colSpan={16} className={styles.emptyState}>
                  {searchTerm.trim() ? "No teams or leagues match your search." : "No Ticket Savings records found."}
                </td>
              </tr>
            ) : null}
          </tbody>
            </table>
          </div>

          {!loading && filteredRows.length ? (
            <div className={styles.pagination}>
              <span>
                Showing {(currentPage - 1) * ROWS_PER_PAGE + 1}–{Math.min(currentPage * ROWS_PER_PAGE, filteredRows.length)} of {filteredRows.length}
              </span>
              <div className={styles.paginationControls}>
                <button
                  type="button"
                  className={styles.pageButton}
                  onClick={() => setCurrentPage((page) => Math.max(1, page - 1))}
                  disabled={currentPage === 1}
                >
                  Previous
                </button>
                <span>Page {currentPage} of {pageCount}</span>
                <button
                  type="button"
                  className={styles.pageButton}
                  onClick={() => setCurrentPage((page) => Math.min(pageCount, page + 1))}
                  disabled={currentPage === pageCount}
                >
                  Next
                </button>
              </div>
            </div>
          ) : null}
        </>
      ) : null}

      {hasImportErrors && importSummary?.errors?.length ? (
        <div className={styles.importErrorView}>
          <div className={styles.importErrorHeader}>
            <div>
              <h2>Import errors</h2>
              <p>Review the failed rows below, then return to the table when you are ready.</p>
            </div>
            <button type="button" className={styles.button} onClick={() => setImportSummary(null)}>
              Back to table
            </button>
          </div>
          <div className={styles.importErrorList}>
            {importSummary.errors.map((item) => (
              <div key={`${item.rowNumber}-${item.league}-${item.team}`} className={styles.importErrorCard}>
                <p>
                  <strong>Row {item.rowNumber}</strong>: {item.league || "-"} / {item.team || "-"} | Price Range{" "}
                  {formatNumber(item.priceRangeMin)} - {formatNumber(item.priceRangeMax, item.priceRangeMaxIsInfinite)}
                </p>
                <p><strong>Reason:</strong> {item.reasons.join(" ")}</p>
                {item.conflicts?.length ? (
                  <div className={styles.conflictList}>
                    {item.conflicts.map((conflict) => (
                      <div key={`${item.rowNumber}-${conflict.id}`} className={styles.conflictCard}>
                        {renderConflictSummary(conflict)}
                      </div>
                    ))}
                  </div>
                ) : null}
              </div>
            ))}
          </div>
        </div>
      ) : null}

      {isImportModalOpen ? (
        <div className={styles.modalBackdrop} role="presentation">
          <div className={`${styles.modal} ${styles.importModal}`} role="dialog" aria-modal="true" aria-labelledby="ticket-savings-import-title">
            <div className={styles.modalHeader}>
              <div>
                <h3 id="ticket-savings-import-title">Import Ticket Savings</h3>
                <p className={styles.subtitle}>Select an Excel file. Records are uploaded only after you click Submit.</p>
              </div>
              <button type="button" className={styles.closeIconButton} onClick={closeImportModal} disabled={importing} aria-label="Close">
                <X size={18} />
              </button>
            </div>

            <div className={styles.modalBody}>
              {importModalError ? (
                <div className={styles.errorBanner}>
                  <AlertCircle size={16} />
                  <span>{importModalError}</span>
                </div>
              ) : null}

              <label className={styles.fileUploadControl}>
                <Upload size={22} />
                <span>Choose Excel file</span>
                <small>.xlsx or .xls</small>
                <input type="file" accept=".xlsx,.xls" onChange={handleImportFileChange} disabled={importing} />
              </label>

              <div className={styles.selectedFileName}>
                <strong>Selected file:</strong>
                <span>{selectedImportFile?.name || "No file selected"}</span>
              </div>
            </div>

            <div className={styles.modalActions}>
              <button type="button" className={styles.closeButton} onClick={closeImportModal} disabled={importing}>Cancel</button>
              <button type="button" className={styles.primaryButton} onClick={() => void handleImportSubmit()} disabled={!selectedImportFile || importing}>
                {importing ? "Uploading..." : "Submit"}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {isModalOpen ? (
        <div className={styles.modalBackdrop} role="presentation">
          <div className={styles.modal}>
            <div className={styles.modalHeader}>
              <div>
                <h3>{editingRow ? "Edit Ticket Savings" : "Add Ticket Savings"}</h3>
                <p className={styles.subtitle}>
                  Show To User fields are percentages. Leave Team empty for a league default (requires Price Range Max = Infinity).
                  Team default = Team set + Price Range Max Infinity.
                </p>
              </div>
              <button type="button" className={styles.closeIconButton} onClick={resetModalState} aria-label="Close">
                <X size={18} />
              </button>
            </div>

            <div className={styles.modalBody}>
              {formErrors.length ? (
                <div className={styles.errorBanner}>
                  <AlertCircle size={16} />
                  <div>
                    {formErrors.map((message) => (
                      <div key={message}>{message}</div>
                    ))}
                  </div>
                </div>
              ) : null}

              {catalogError ? (
                <div className={styles.errorBanner}>
                  <AlertCircle size={16} />
                  <span>{catalogError}</span>
                </div>
              ) : null}

              <div className={styles.formGrid}>
                <label className={styles.field}>
                  League
                  <select value={form.league} onChange={handleLeagueChange} disabled={catalogLoading}>
                    <option value="">{catalogLoading ? "Loading leagues..." : "Select league"}</option>
                    {leagueOptions.map((league) => (
                      <option key={league} value={league}>{league}</option>
                    ))}
                  </select>
                </label>
                <label className={styles.field}>
                  Team
                  <select value={form.team} onChange={handleTeamChange} disabled={!form.league || catalogLoading}>
                    <option value="">
                      {!form.league
                        ? "Select a league first"
                        : catalogLoading
                          ? "Loading teams..."
                          : "League default (leave empty)"}
                    </option>
                    {teamOptions.map((team) => (
                      <option key={team} value={team}>{team}</option>
                    ))}
                  </select>
                </label>
                <label className={styles.field}>
                  Low
                  <input inputMode="decimal" value={form.low} onChange={handleFormChange("low")} placeholder="Optional: 14, 14.00%, or No data" />
                </label>
                <label className={styles.field}>
                  High
                  <input inputMode="decimal" value={form.high} onChange={handleFormChange("high")} placeholder="Optional: 23, 23.00%, or No data" />
                </label>
                <label className={styles.field}>
                  Median
                  <input inputMode="decimal" value={form.median} onChange={handleFormChange("median")} placeholder="Optional: 19, 19.00%, or No data" />
                </label>
                <label className={styles.field}>
                  Recommended
                  <input value={form.recommended} onChange={handleFormChange("recommended")} />
                </label>
                <label className={styles.field}>
                  Show To User Min (%)
                  <input type="number" min="0" max="100" step="0.01" value={form.showToUserMin} onChange={handleFormChange("showToUserMin")} />
                </label>
                <label className={styles.field}>
                  Show To User Max (%)
                  <input type="number" min="0" max="100" step="0.01" value={form.showToUserMax} onChange={handleFormChange("showToUserMax")} />
                  <span className={styles.fieldHint}>For a fixed default % set Min and Max to the same value (e.g. 3 and 3).</span>
                </label>
                <label className={styles.field}>
                  Price Range Min
                  <input type="number" min="0" step="0.01" value={form.priceRangeMin} onChange={handleFormChange("priceRangeMin")} />
                </label>
                <label className={styles.field}>
                  Price Range Max
                  <input value={form.priceRangeMax} onChange={handleFormChange("priceRangeMax")} placeholder="Enter number or Infinity" />
                  <span className={styles.fieldHint}>Example: `75` or `Infinity` (defaults use 0–Infinity).</span>
                </label>
              </div>

              {conflictDetails?.conflicts?.length ? (
                <div className={styles.conflictList}>
                  {conflictDetails.conflicts.map((conflict) => (
                    <div key={conflict.id} className={styles.conflictCard}>
                      {renderConflictSummary(conflict)}
                    </div>
                  ))}
                </div>
              ) : null}
            </div>

            <div className={styles.modalActions}>
              <button type="button" className={styles.closeButton} onClick={resetModalState}>
                Cancel
              </button>
              <button type="button" className={styles.primaryButton} onClick={() => void handleSave()} disabled={saving}>
                {saving ? "Saving..." : "Save"}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default BuyAdminTicketSavings;
